/**
 * PainelEvento.tsx — S2 (06/07/2026) · UNIFICADO 08/07/2026
 * Painel consolidado do evento: matriz fase × time agregando cards de
 * execução (status) + checklists dos cards-mãe (checks e INFOs).
 * Fonte: GET /eventos/:id/painel.
 *
 * Unificação (decisão Renan 08/07): esta aba absorveu a antiga "Execução" —
 * filtros de polo/tipo/data na lateral, countdown D-N, % por time — e o clique
 * num card abre o drawer de detalhes (EditDemandaModalV3) POR CIMA da página,
 * sem navegar pra tabela de demandas.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertCircle, CheckCircle2, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { EditDemandaModalV3 } from '@/components/EditDemandaModalV3';
import type { Evento, TipoEvento } from '@/lib/types';

interface PainelInfoPendente {
  demandaId: string;
  itemId: string;
  texto: string;
  estado: string;
  areaDestino?: string;
  payload?: string;
}
interface PainelCard {
  id: string;
  titulo: string;
  status: string;
  prazoSla?: string;
  tipoDemanda?: string;
}
interface PainelTime {
  areaId: string;
  areaNome: string;
  total: number;
  done: number;
  cards: PainelCard[];
  infosPendentes: PainelInfoPendente[];
}
interface PainelData {
  eventoId: string;
  galeriaUrl?: string;
  fases: Record<'pre' | 'durante' | 'pos', { porTime: PainelTime[] }>;
}

const FASE_LABEL: Record<'pre' | 'durante' | 'pos', string> = {
  pre: 'Pré-evento',
  durante: 'Durante',
  pos: 'Pós-evento',
};

const STATUS_COR: Record<string, string> = {
  'Triagem': 'bg-gray-500/15 text-text-2 border-border',
  'Em produção': 'bg-blue/15 text-blue-light border-blue/40',
  'Em revisão': 'bg-purple-500/15 text-purple-300 border-purple-500/40',
  'Aguardando aprovação': 'bg-yellow/15 text-yellow border-yellow/40',
  'Publicada': 'bg-green/15 text-green border-green/40',
};

// Cards de verificação MAC usam o fluxo enxuto — mesmo relabel do card (V3)
const VERIF_LABEL_PAINEL: Record<string, string> = {
  'Triagem': 'A verificar',
  'Em revisão': 'Revisão',
  'Publicada': 'Concluído',
};
function statusLabelPainel(cd: PainelCard): string {
  return cd.tipoDemanda === 'verificacao_mac' ? (VERIF_LABEL_PAINEL[cd.status] ?? cd.status) : cd.status;
}

const TIPO_EVENTO_LABELS: Record<string, string> = {
  proprietario: 'Proprietário',
  patrocinado: 'Patrocinado',
  setorial: 'Setorial',
  interno: 'Interno',
  institucional: 'Institucional',
  feriado: 'Feriado',
  comercial: 'Comercial',
  terceiros: 'Terceiros',
  mining_show: 'Mining Show',
  experiencia: 'Experiência',
};

// Polos reconhecidos: 27 UFs brasileiras + polos internacionais (NY, PT/Lisboa).
const UFS_BR = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];
const POLOS = [...UFS_BR, 'NY', 'PT'];

/** Polo (UF) do evento: usa o campo `uf`; fallback: deriva do texto `local`. */
function ufDoEvento(ev: Evento): string | null {
  if (ev.uf) return ev.uf.toUpperCase();
  const tokens = (ev.local ?? '').toUpperCase().match(/\b[A-Z]{2}\b/g);
  if (tokens) for (const t of tokens) if (POLOS.includes(t)) return t;
  return null;
}

function diasAte(iso?: string): number {
  if (!iso) return 999;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
}
function countdownColor(dias: number): string {
  if (dias < 5) return 'text-red';
  if (dias <= 15) return 'text-yellow';
  return 'text-green';
}

export function PainelEventoPanel({ eventos, initialEventoId }: { eventos: Evento[]; initialEventoId?: string | null }) {
  const [selectedId, setSelectedId] = useState<string | null>(initialEventoId ?? null);
  useEffect(() => { if (initialEventoId) setSelectedId(initialEventoId); }, [initialEventoId]);

  // Drawer do card (aberto por cima do painel — não navega)
  const [editId, setEditId] = useState<string | null>(null);
  const qc = useQueryClient();

  // Filtros da lateral (herdados da antiga aba Execução)
  const [filtroTipoEvento, setFiltroTipoEvento] = useState<string>('todos');
  const [filtroPolo, setFiltroPolo] = useState<string>('todos');
  const [filtroTime, setFiltroTime] = useState<string>('todos');
  const [dataDe, setDataDe] = useState<string>('');
  const [dataAte, setDataAte] = useState<string>('');

  const baseEventos = useMemo(
    () => eventos.filter((e) =>
      e.status !== 'Realizado' && e.status !== 'Cancelado' && e.tipoEvento !== 'feriado'),
    [eventos],
  );

  const polosDisponiveis = useMemo(() => {
    const set = new Set<string>();
    for (const e of baseEventos) { const uf = ufDoEvento(e); if (uf) set.add(uf); }
    return [...set].sort();
  }, [baseEventos]);

  const tiposDisponiveis = useMemo(() => {
    const set = new Set<TipoEvento>();
    for (const e of baseEventos) if (e.tipoEvento) set.add(e.tipoEvento);
    return [...set];
  }, [baseEventos]);

  const filtrosAtivos = filtroTipoEvento !== 'todos' || filtroPolo !== 'todos' || !!dataDe || !!dataAte;
  function limparFiltros() {
    setFiltroTipoEvento('todos'); setFiltroPolo('todos'); setDataDe(''); setDataAte('');
  }

  const upcoming = useMemo(() => baseEventos
    .filter((e) => filtroTipoEvento === 'todos' || e.tipoEvento === filtroTipoEvento)
    .filter((e) => filtroPolo === 'todos' || ufDoEvento(e) === filtroPolo)
    .filter((e) => {
      if (!dataDe && !dataAte) return true;
      const d = e.dataInicio ? e.dataInicio.slice(0, 10) : '';
      if (!d) return false;
      if (dataDe && d < dataDe) return false;
      if (dataAte && d > dataAte) return false;
      return true;
    })
    .sort((a, b) => new Date(a.dataInicio ?? '').getTime() - new Date(b.dataInicio ?? '').getTime()),
  [baseEventos, filtroTipoEvento, filtroPolo, dataDe, dataAte]);

  const activeId = selectedId ?? upcoming[0]?.id ?? null;
  const active = eventos.find((e) => e.id === activeId) ?? null;

  const painelQ = useQuery({
    queryKey: ['painel', activeId],
    queryFn: () => (activeId ? api.eventos.painel(activeId).then((r) => r.data) : null),
    enabled: !!activeId,
    staleTime: 30_000,
  });
  const painel = painelQ.data as PainelData | null;

  // Dados pro drawer do card (mesmas queryKeys do resto do app — cache compartilhado)
  const demandasQ = useQuery({ queryKey: ['demandas'], queryFn: () => api.demandas.list().then((r) => r.data) });
  const solicitantesQ = useQuery({ queryKey: ['solicitantes'], queryFn: () => api.solicitantes.list().then((r) => r.data) });
  const pessoasQ = useQuery({ queryKey: ['pessoas'], queryFn: () => api.pessoas.list().then((r) => r.data) });
  const areasQ = useQuery({ queryKey: ['areas'], queryFn: () => api.areas.list().then((r) => r.data) });

  // Progresso geral do evento (todas as fases)
  const progressoGeral = useMemo(() => {
    if (!painel) return null;
    let total = 0, done = 0;
    (['pre', 'durante', 'pos'] as const).forEach((f) => {
      for (const t of painel.fases[f]?.porTime ?? []) { total += t.total; done += t.done; }
    });
    return total > 0 ? Math.round((done / total) * 100) : 0;
  }, [painel]);

  const timesDisponiveis = useMemo(() => {
    if (!painel) return [];
    const set = new Set<string>();
    (['pre', 'durante', 'pos'] as const).forEach((f) => {
      for (const t of painel.fases[f]?.porTime ?? []) set.add(t.areaNome);
    });
    return [...set].sort();
  }, [painel]);

  function fecharDrawer() {
    setEditId(null);
    // Status/checklist podem ter mudado no drawer — atualiza o painel
    qc.invalidateQueries({ queryKey: ['painel', activeId] });
  }

  return (
    <div className="grid grid-cols-[240px_1fr] border border-border rounded-2xl overflow-hidden" style={{ minHeight: '480px' }}>
      {/* Sidebar: eventos + filtros (unificado com a antiga Execução) */}
      <div className="bg-panel-2 border-r border-border overflow-y-auto">
        <div className="px-4 py-3 border-b border-border space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold">Painel do evento</div>
            {filtrosAtivos && (
              <button type="button" onClick={limparFiltros}
                className="text-[9px] text-blue-light hover:underline">limpar</button>
            )}
          </div>
          <div className="text-[10px] text-muted">{upcoming.length} evento{upcoming.length !== 1 ? 's' : ''} ativos</div>

          <div className="grid grid-cols-2 gap-1.5">
            <select className="input !py-1 !px-1.5 text-[10px]" value={filtroPolo}
              onChange={(e) => { setFiltroPolo(e.target.value); setSelectedId(null); }}
              title="Polo (Estado)">
              <option value="todos">Polo: todos</option>
              {polosDisponiveis.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
            </select>
            <select className="input !py-1 !px-1.5 text-[10px]" value={filtroTipoEvento}
              onChange={(e) => { setFiltroTipoEvento(e.target.value); setSelectedId(null); }}
              title="Tipo de evento">
              <option value="todos">Tipo: todos</option>
              {tiposDisponiveis.map((t) => (
                <option key={t} value={t}>{TIPO_EVENTO_LABELS[t] ?? t}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <label className="flex flex-col gap-0.5">
              <span className="text-[8px] uppercase tracking-wider text-muted">De</span>
              <input type="date" className="input !py-1 !px-1.5 text-[10px]" value={dataDe}
                onChange={(e) => { setDataDe(e.target.value); setSelectedId(null); }} />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[8px] uppercase tracking-wider text-muted">Até</span>
              <input type="date" className="input !py-1 !px-1.5 text-[10px]" value={dataAte}
                onChange={(e) => { setDataAte(e.target.value); setSelectedId(null); }} />
            </label>
          </div>
        </div>
        {upcoming.map((ev) => {
          const dias = diasAte(ev.dataInicio);
          const isActive = ev.id === activeId;
          return (
            <button key={ev.id} type="button" onClick={() => setSelectedId(ev.id)}
              className={clsx(
                'w-full text-left px-4 py-2.5 border-b border-border/50 transition-colors',
                isActive ? 'bg-blue/10 border-l-2 border-l-blue' : 'hover:bg-panel',
              )}>
              <div className="text-[11px] font-semibold leading-tight">{ev.nome}</div>
              <div className="flex items-center gap-2 text-[9px] text-muted mt-0.5">
                <span>{ev.dataInicio ? new Date(ev.dataInicio).toLocaleDateString('pt-BR') : 'sem data'} · {ev.status ?? 'Planejado'}</span>
                <span className={clsx('font-bold ml-auto', countdownColor(dias))}>
                  {dias > 0 ? `${dias}d` : dias === 0 ? 'Hoje' : 'Passou'}
                </span>
              </div>
            </button>
          );
        })}
        {upcoming.length === 0 && (
          <div className="px-4 py-6 text-xs text-muted text-center">Nenhum evento ativo.</div>
        )}
      </div>

      {/* Painel: fase × time */}
      <div className="bg-panel overflow-y-auto">
        {!active && <div className="p-4 text-xs text-muted">Selecione um evento.</div>}

        {active && (
          <div className="flex justify-between items-start px-4 py-3 border-b border-border">
            <div>
              <div className="text-sm font-bold text-text">{active.nome}</div>
              <div className="text-[10px] text-muted mt-0.5">
                {active.dataInicio ? new Date(active.dataInicio).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—'}
                {active.local && ` · ${active.local}`}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {timesDisponiveis.length > 0 && (
                <select className="input !py-0.5 !px-2 text-[10px]" value={filtroTime}
                  onChange={(e) => setFiltroTime(e.target.value)} title="Filtrar por time">
                  <option value="todos">Todos os times</option>
                  {timesDisponiveis.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              )}
              <span className={clsx('text-[10px] px-2 py-0.5 rounded-full font-bold',
                active.status === 'Confirmado' ? 'bg-green/15 text-green' : 'bg-blue/15 text-blue-light')}>
                {active.status ?? 'Planejado'}
              </span>
              {progressoGeral !== null && (
                <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-panel-2 text-text border border-border">
                  {progressoGeral}% geral
                </span>
              )}
            </div>
          </div>
        )}

        <div className="p-4 space-y-4">
          {active && painelQ.isLoading && (
            <div className="flex items-center gap-2 text-xs text-muted"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Carregando painel…</div>
          )}
          {active && painel?.galeriaUrl && (
            <a
              href={painel.galeriaUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 text-[11px] font-semibold text-blue-light border border-blue/40 bg-blue/10 rounded-lg px-3 py-2 hover:bg-blue/20 transition-colors"
            >
              Galeria de fotos pós-evento — abrir link do fotógrafo ↗
            </a>
          )}
          {active && painel && (['pre', 'durante', 'pos'] as const).map((fase) => {
            const times = (painel.fases[fase]?.porTime ?? [])
              .filter((t) => filtroTime === 'todos' || t.areaNome === filtroTime);
            if (times.length === 0) return null;
            const total = times.reduce((s, t) => s + t.total, 0);
            const done = times.reduce((s, t) => s + t.done, 0);
            return (
              <section key={fase}>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-[10px] font-bold uppercase tracking-wider text-text-2">{FASE_LABEL[fase]}</h3>
                  <span className="text-[10px] text-muted">{done}/{total}</span>
                  <div className="flex-1 h-1 rounded bg-border overflow-hidden">
                    <div className="h-full bg-blue transition-all" style={{ width: total > 0 ? `${(done / total) * 100}%` : '0%' }} />
                  </div>
                </div>
                <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                  {times.map((t) => (
                    <TimeCard key={`${fase}-${t.areaId}`} eventoId={active.id} time={t} onOpenCard={setEditId} />
                  ))}
                </div>
              </section>
            );
          })}
          {active && painel && (['pre', 'durante', 'pos'] as const).every((f) => (painel.fases[f]?.porTime ?? []).length === 0) && (
            <div className="text-xs text-muted">
              Nenhum card vinculado a este evento ainda. Confirme o evento (no calendário) para gerar os cards pelo template.
            </div>
          )}
        </div>
      </div>

      {/* Drawer do card — abre por cima do painel, sem navegar */}
      <EditDemandaModalV3
        demandaId={editId}
        demandas={demandasQ.data ?? []}
        solicitantes={solicitantesQ.data ?? []}
        pessoas={pessoasQ.data ?? []}
        areas={areasQ.data ?? []}
        onClose={fecharDrawer}
      />
    </div>
  );
}

function TimeCard({ eventoId, time, onOpenCard }: { eventoId: string; time: PainelTime; onOpenCard: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const pct = time.total > 0 ? Math.round((time.done / time.total) * 100) : 0;
  const infos = time.infosPendentes ?? [];

  return (
    <div className="border border-border rounded-lg bg-panel-2 p-3 space-y-2">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 text-left">
        {open ? <ChevronDown className="w-3 h-3 text-muted shrink-0" /> : <ChevronRight className="w-3 h-3 text-muted shrink-0" />}
        <span className="text-[11px] font-bold flex-1">{time.areaNome}</span>
        {infos.length > 0 && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-yellow/15 text-yellow border border-yellow/40">
            {infos.length} INFO{infos.length > 1 ? 's' : ''}
          </span>
        )}
        <span className={clsx('text-[10px] font-semibold', pct === 100 ? 'text-green' : 'text-text-2')}>{time.done}/{time.total}</span>
      </button>
      <div className="h-1 rounded bg-border overflow-hidden">
        <div className={clsx('h-full transition-all', pct === 100 ? 'bg-green' : 'bg-blue')} style={{ width: `${pct}%` }} />
      </div>

      {open && (
        <div className="space-y-1.5 pt-1">
          {(time.cards ?? []).map((cd) => (
            // Unificação 08/07: abre o drawer do card in-place (antes navegava pra /demandas)
            <button key={cd.id} type="button" onClick={() => onOpenCard(cd.id)}
              className="w-full flex items-center gap-2 text-[10px] hover:bg-panel rounded px-1 -mx-1 transition-colors text-left">
              <span className="flex-1 truncate" title={cd.titulo}>{cd.titulo}</span>
              {cd.prazoSla && <span className="text-muted shrink-0">{new Date(`${cd.prazoSla}T12:00:00`).toLocaleDateString('pt-BR')}</span>}
              <span className={clsx('px-1.5 py-0.5 rounded border text-[9px] font-semibold shrink-0', STATUS_COR[cd.status] ?? 'bg-panel text-text-2 border-border')}>
                {statusLabelPainel(cd)}
              </span>
            </button>
          ))}
          {infos.map((info) => <InfoItem key={info.itemId} eventoId={eventoId} info={info} />)}
          {(time.cards ?? []).length === 0 && infos.length === 0 && (
            <div className="text-[10px] text-muted">Só checks do card-mãe nesta fase.</div>
          )}
        </div>
      )}
    </div>
  );
}

function InfoItem({ eventoId, info }: { eventoId: string; info: PainelInfoPendente }) {
  const qc = useQueryClient();
  const [payload, setPayload] = useState(info.payload ?? '');
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (vars: { estado: string; payload?: string }) =>
      api.eventos.checklistItem(eventoId, info.demandaId, info.itemId, vars),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ['painel', eventoId] });
      qc.invalidateQueries({ queryKey: ['demandas'] });
    },
    onError: (e) => {
      setErr(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? e.message) : String(e));
    },
  });

  return (
    <div className="border border-yellow/40 bg-yellow/5 rounded p-2 space-y-1.5">
      <div className="flex items-start gap-1.5 text-[10px]">
        <AlertCircle className="w-3 h-3 text-yellow mt-0.5 shrink-0" />
        <span className="flex-1 leading-tight">{info.texto}</span>
        <span className="text-[9px] text-yellow font-semibold uppercase shrink-0">{info.estado}</span>
      </div>
      {info.estado === 'pendente' && (
        <>
          <textarea
            className="input w-full text-[10px] !py-1 !px-1.5 min-h-[40px]"
            placeholder="Conteúdo da informação (ex.: lista de nomes)…"
            value={payload}
            onChange={(e) => setPayload(e.target.value)}
          />
          <button type="button" disabled={mut.isPending || !payload.trim()}
            onClick={() => mut.mutate({ estado: 'informado', payload })}
            className="btn-primary !py-1 !px-2.5 text-[10px] disabled:opacity-50">
            {mut.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Marcar informado'}
          </button>
        </>
      )}
      {info.estado === 'informado' && (
        <div className="space-y-1.5">
          <p className="text-[9px] text-muted">Aplicar durante o evento — ação do time destino.</p>
          {info.payload && <p className="text-[10px] text-text-2 bg-panel rounded px-1.5 py-1 whitespace-pre-wrap">{info.payload}</p>}
          <button type="button" disabled={mut.isPending}
            onClick={() => mut.mutate({ estado: 'aplicado' })}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-semibold bg-green text-white hover:bg-green/80 disabled:opacity-50">
            {mut.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle2 className="w-3 h-3" /> Marcar aplicado</>}
          </button>
        </div>
      )}
      {err && <p className="text-[9px] text-red">{err}</p>}
    </div>
  );
}
